'use client';

import { IRedux } from '@/_store/types';
import { Box, Button, Center, Text } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import BrandTabs from './BrandTabs';
import Products from './Products';
import Bill from './Bill';
import { useTranslations } from 'next-intl';
import { useRouter } from '@/i18n/navigation';
import Header from './Header';
import Favourites from '@/_components/Favourites';
import { getBrandHref } from '@/_lib/brand-href';

export default function CartContent() {
  const t = useTranslations();
  const router = useRouter();

  const cartState = useSelector((state: IRedux) => state.cart.cart);

  // Get all brands with products
  const brandsWithProducts = useMemo(() => {
    return Object.entries(cartState || {})
      .filter(([_, data]) => data?.products?.length > 0)
      .map(([shipperId]) => shipperId);
  }, [cartState]);

  // State for selected brand
  const [selectedShipperId, setSelectedShipperId] = useState<string>('');

  // Utensils count state
  const [utensilsCount, setUtensilsCount] = useState(0);

  // Set initial selected brand
  useEffect(() => {
    if (brandsWithProducts.length > 0 && !brandsWithProducts.includes(selectedShipperId)) {
      setSelectedShipperId(brandsWithProducts[0]);
    }
  }, [brandsWithProducts, selectedShipperId]);


  const cart = cartState?.[selectedShipperId]?.products || [];
  const [totalPrice, setTotalPrice] = useState(0);

  useEffect(() => {
    if (cart.length > 0) {
      let total = 0;
      cart.forEach((product) => {
        total += (product.price_with_discount || 0) * product.quantity;
        if (product?.order_modifiers?.length > 0) {
          for (const modifier of product.order_modifiers) {
            total +=
              +modifier.modifier_price *
              +(modifier?.modifier_quantity * product.quantity);
          }
        }
      });
      setTotalPrice(total);
    }
  }, [cart]);

  // If no products in cart, show empty state
  if (brandsWithProducts.length === 0) {
    return (
      <Center
        flexDir="column"
        alignItems="center"
        justifyContent="center"
        h="80vh"
      >
        <Text fontSize="lg" fontWeight="bold" textAlign="center">
          {t('your_cart_empty')}
        </Text>
        <Text fontSize="sm">
          {t('the_items_you_order_will_appear_here')}
        </Text>
        <Button
          onClick={() => router.push('/')}
          bg="primary"
          color="primary.text"
          _hover={{ bg: 'primary.hover' }}
          rounded={12}
          size="xl"
          mt={4}
        >
          {t('back_to_menu')}
        </Button>
      </Center>
    );
  }

  return (
    <Box>
      <Header selectedShipperId={selectedShipperId} />
      <BrandTabs
        selectedShipperId={selectedShipperId}
        onSelectBrand={setSelectedShipperId}
      />

      <Box mb="120px">
        <Products totalPrice={totalPrice} selectedShipperId={selectedShipperId} />

        {/* Utensils Section */}
        {/* <Stack
          mt={4}
          px={4}
          separator={<StackSeparator borderColor="background" />}
        >
          <Flex
            gap={3}
            alignItems="center"
            justifyContent="space-between"
            py={2}
          >
            <Flex gap={3} alignItems="center">
              <Flex
                w="48px"
                h="48px"
                bg="background"
                rounded={12}
                alignItems="center"
                justifyContent="center"
              >
                <CutleryIcon size={24} />
              </Flex>
              <Text fontSize="sm" fontWeight={500}>
                {t('utensils')}
              </Text>
            </Flex>
            <Counter
              width="108px"
              height="32px"
              bg="background"
              variable={utensilsCount}
              onDecrease={() => setUtensilsCount(Math.max(0, utensilsCount - 1))}
              onIncrease={() => setUtensilsCount(utensilsCount + 1)}
            />
          </Flex>
        </Stack> */}

        <Box px={4} mt={4}>
          <Button
            w="full"
            variant="subtle"
            rounded={12}
            h="48px"
            color="text"
            fontWeight={500}
            onClick={() => router.push(getBrandHref(selectedShipperId))}
          >
            {t('open_restaurant_menu')}
          </Button>
        </Box>

        <Favourites selectedShipperId={selectedShipperId} />
      </Box>

      {/* Bill / Footer */}
      <Bill totalPrice={totalPrice} selectedShipperId={selectedShipperId} />
    </Box>
  );
}
